import ReactGridLayout from "react-grid-layout";
import { UserWidget, WidgetLayout } from "../constants/online.homepagegateway.api";
import { WidgetId } from "../constants/widgetId";
import { WidgetLayoutType } from "../constants/widgetLayoutType"
import { AppState } from "@/react-app-env";

export const getWidgetsInCurrentState = (state: AppState) => state?.widgetsState.widgets;
export const getWidgetsLayoutInCurrentState = (state: AppState) => state?.widgetsState.widgetsLayout;
export const getWindowWidthInCurrentState = (state: AppState) => state?.widgetsState.windowWidth;

export const newWidgetsSessionStorageKey = 'myNewWidgets'

export const GRID_LAYOUT_GRID_ROW_HEIGHT = 1;
export const SINGLE_TILE_LAYOUT_MIN_WINDOW_WIDTH = 1241;
export const DEFAULT_WIDGET_HEIGHT = 500;

export const getWidgetPadding = (windowWidth: number) => {
    if (windowWidth < 600) {
        return {left: 0, bottom: 8};
    }

    if (windowWidth <= 1440 && windowWidth >= 600) {
        return {left: 24, bottom: 24};
    }

    return {left: 32, bottom: 32};
}

export const getGridColumns = (windowWidth: number) => {
    return windowWidth < SINGLE_TILE_LAYOUT_MIN_WINDOW_WIDTH ? 1 : 12;
}

export const getWidgetLayoutType = (windowWidth: number) => {
    return windowWidth < SINGLE_TILE_LAYOUT_MIN_WINDOW_WIDTH ? WidgetLayoutType.SingleTileLayout : WidgetLayoutType.Default
}

export const getItemWidth = (widgetId: WidgetId, gridColumns: number) => {
    if (gridColumns == 1) {
        return 1;
    }
    if (widgetId == WidgetId.RecentOrders) {
        return (2/3) * gridColumns;
    }
    return (1/3) * gridColumns; 
}

export const layoutsNotInDb = (userWidgets: UserWidget[]) => {
    // first visit to new home page
    let count = 0;
    userWidgets.forEach((userWidget) => {
        if (userWidget.layouts.length === 0) {
            count++;
        }
    });

    return count === userWidgets.length;
}

export const buildNewLayouts = (userWidgets: UserWidget[]) => {
    let widgets = buildnewLayout(userWidgets, 1, WidgetLayoutType.SingleTileLayout);
    widgets = buildnewLayout(widgets, 12, WidgetLayoutType.Default);
    return widgets;
}

const buildnewLayout = (userWidgets: UserWidget[], gridColumns: number, layoutType: WidgetLayoutType) => {
    let currentRow = 0;
    let currentX = 0;
    let id = 1;

    const updatedWidgets = userWidgets.map((userWidget) => {
        userWidget.id = id;
        id++;
  
        const dbLayouts = userWidget.layouts;

        const itemWidth = getItemWidth(userWidget.widgetId, gridColumns);
        if (currentX + itemWidth > gridColumns) {
            currentRow++;
            currentX = 0;
        }
  
        const dbLayout = {
            id: userWidget.id.toString(),
            x: currentX,
            y: currentRow * GRID_LAYOUT_GRID_ROW_HEIGHT,
            layoutType: layoutType
        }
  
        const currentLayout = {
          id: dbLayout.id,
          x: dbLayout.x,
          y: dbLayout.y,
          width: itemWidth,
          height: DEFAULT_WIDGET_HEIGHT,
          forceRender: 0,
          layoutType: dbLayout.layoutType
        };
  
        currentX += itemWidth;
        dbLayouts.push(dbLayout);

        return {
          ... userWidget, 
          currentLayout: currentLayout,
          layouts:dbLayouts
        }
    });

    return updatedWidgets;
}

export const buildInitWidgetLayout = (userWidgets: UserWidget[], windowWidth: number) : UserWidget[] => {
    const gridColumns = getGridColumns(windowWidth);
    const layoutType = getWidgetLayoutType(windowWidth);
    let maxY = 0;
    let currentX = 0;

    const updatedWidgets = userWidgets.map((userWidget) => {
      let dbLayout;
      const dbLayouts = userWidget.layouts;
      const itemWidth = getItemWidth(userWidget.widgetId, gridColumns);

      dbLayout = dbLayouts.find((layout: WidgetLayout) => layout.layoutType === layoutType);

      if (!dbLayout) { // if there is no dbLayout for this layoutype, it means user has added widget in other layout type
        if (currentX + itemWidth > gridColumns) {
            currentX = 0;
        }
        maxY = maxY + 1;
        userWidget.id = Number(dbLayouts[0].id);
        dbLayout = {
            id: userWidget.id.toString(),
            x: currentX,
            y: maxY,
            layoutType: layoutType
        }
        currentX += itemWidth;
        dbLayouts.push(dbLayout);
      } else {
        userWidget.id = Number(dbLayout.id);
        maxY = Math.max(maxY, dbLayout.y); // this will work fine without going throug all elements because, newly added widgets are always at bottom
      }

      const currentLayout = {
        id: dbLayout.id,
        x: dbLayout.x,
        y: dbLayout.y,
        width: itemWidth,
        height: DEFAULT_WIDGET_HEIGHT,
        forceRender: 0,
        layoutType: dbLayout.layoutType
      };

      return {
        ... userWidget, 
        currentLayout: currentLayout,
        layouts:dbLayouts
      }
    });

    return updatedWidgets;
}

export const getWidgetsAfterResize = (userWidgets: UserWidget[], resizeDetail: any, windowWidth: number) => {
    const padding = getWidgetPadding(windowWidth);
    return userWidgets.map((userWidget: UserWidget) => {
        if (userWidget.id === resizeDetail.id) {
            const layout = userWidget.currentLayout;
            return {
                ... userWidget, 
                currentLayout: {
                    ... layout, 
                    height: (resizeDetail.newHeight / GRID_LAYOUT_GRID_ROW_HEIGHT) + padding.bottom
                }
            }
        }
        return { ...userWidget };
    });
}

export const updateWidgetsOnDragStop = (userWidgets: UserWidget[], newLayoutArray: any) => {
    const updatedWidgets: UserWidget[] = [];
    newLayoutArray.forEach((newLayout: any) => {
        const userWidget = userWidgets.find((userWidget: any) => newLayout.i === userWidget.currentLayout.id);
        userWidget!.currentLayout = {
            id: newLayout.i,
            x: newLayout.x,
            y: newLayout.y,
            width: newLayout.w,
            height: newLayout.h,
            forceRender: userWidget!.currentLayout.forceRender, 
            layoutType: userWidget!.currentLayout.layoutType
        };
        updatedWidgets.push(userWidget!);
    });

    return updatedWidgets;
}

export const getReactGridLayoutArray = (userWidgets: UserWidget[]) => {
    const layoutArray: ReactGridLayout.Layout[] = [];
    userWidgets.forEach((userWidget) => {
      const layout = {
        i: userWidget.currentLayout.id,
        x: userWidget.currentLayout.x,
        y: userWidget.currentLayout.y,
        w: userWidget.currentLayout.width,
        h: userWidget.currentLayout.height,
      }
      layoutArray.push(layout);
    });
    return layoutArray;
}

export const saveLayoutsToDatabase = (userWidgets: UserWidget[], newLayoutArray: any, windowWidth: number) => {
    const layoutType = getWidgetLayoutType(windowWidth);

    const updatedWidgets: UserWidget[] = [];
    newLayoutArray.forEach((newLayout: any) => {
        const userWidget = userWidgets.find((userWidget: any) => newLayout.i === userWidget.currentLayout.id);
        const dbLayout = userWidget!.layouts.find((layout: WidgetLayout) => layout.layoutType === layoutType);
        dbLayout!.x = newLayout.x;
        dbLayout!.y = newLayout.y;
        
        updatedWidgets.push(userWidget!);
    });
    return updatedWidgets;
}

export const setCurrentLayout = (userWidgets: UserWidget[], windowWidth: number) => {
    const layoutType = getWidgetLayoutType(windowWidth);
    const gridColumns = getGridColumns(windowWidth);
    return userWidgets.map((userWidget: UserWidget) => {
        const layout = userWidget.layouts.find((layout: WidgetLayout) => layout.layoutType === layoutType);
        return {
            ...userWidget,
            currentLayout: {
                ...userWidget.currentLayout,
                x: layout!.x,
                y: layout!.y,
                width: getItemWidth(userWidget.widgetId, gridColumns),
                layoutType: layout!.layoutType
            }
        }
    })
}

export const addWidget = (userWidgets: UserWidget[], widget: UserWidget, windowWidth: number) => {
    const gridColumns = getGridColumns(windowWidth);
    const layoutType = getWidgetLayoutType(windowWidth);
    const otherLayoutType = layoutType === WidgetLayoutType.Default ? WidgetLayoutType.SingleTileLayout : WidgetLayoutType.Default;

    let maxY = 0;
    let maxY_OtherLayout = 0;
    userWidgets.forEach((userWidget: UserWidget) => {
        const otherLayout = userWidget.layouts.find((l: WidgetLayout) => l.layoutType === otherLayoutType);
        maxY = Math.max(maxY, userWidget.currentLayout.y + userWidget.currentLayout.height);
        maxY_OtherLayout = Math.max(maxY_OtherLayout, otherLayout!.y);
    });

    if (userWidgets.length > 0) {
        const lastWidget = userWidgets.reduce((prev, current) => (prev && prev.id > current.id) ? prev : current);
        widget.id = lastWidget.id + 1;
    } 
    else {
        widget.id = 1;
    }

    
    widget.layouts = [{
        id: widget.id.toString(),
        x: 0,
        y: maxY,
        layoutType: layoutType
    },
    {
        id: widget.id.toString(),
        x: 0,
        y: maxY_OtherLayout + 1,
        layoutType: otherLayoutType
    }]

    widget.currentLayout = {
        id: widget.id.toString(),
        x: 0,
        y: maxY,
        width: getItemWidth(widget.widgetId, gridColumns),
        height: DEFAULT_WIDGET_HEIGHT,
        forceRender: 0, 
        layoutType: layoutType
    }

    return [ ...userWidgets, widget ];
}

export const updateWidget = (userWidgets: UserWidget[], widget: UserWidget) => {
    return userWidgets.map((userWidget) => {
        if (userWidget.id === widget.id) {
          return { 
            ...widget,
            currentLayout: {
                ...userWidget.currentLayout,
                forceRender: userWidget.currentLayout.forceRender + 1
            }
          };
        }
        return { ...userWidget};
    });
}

export const deleteWidget = (userWidgets: UserWidget[], widget: UserWidget) => {
    return userWidgets.filter(userWidget => userWidget.id !== widget.id);
}